import { formatPhoneNumber } from "lopermut"
import { Copy } from "lucide-react"
import * as React from "react"
import { useCopyToClipboard } from "../hooks/useCopyToClipboard"
import { cn } from "../utils"

export interface PhoneProps extends React.HTMLAttributes<HTMLDivElement> {
  phone?: string | null
  showCopy?: boolean
  showLink?: boolean
  copyMessage?: string
  countryCode?: string
  locale?: string
}

function Phone({
  className,
  phone,
  showCopy = false,
  showLink = false,
  copyMessage = "Phone number copied to clipboard",
  countryCode,
  locale,
  ...props
}: PhoneProps) {
  const { isCopied, onCopy } = useCopyToClipboard({
    text: phone || "",
    copyMessage,
    errorMessage: "Failed to copy phone number",
  })

  if (!phone) {
    return (
      <div className={cn("flex items-center gap-2 text-muted-foreground", className)} {...props}>
        <span className="text-sm">-</span>
      </div>
    )
  }

  const formattedPhone = formatPhoneNumber(phone, countryCode, locale)

  const content = (
    <>
      <span>{formattedPhone}</span>
      {showCopy && (
        <button
          onClick={onCopy}
          className={cn(
            "opacity-0 group-hover:opacity-100 transition-opacity",
            "flex items-center justify-center w-5 h-5 rounded",
            "hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
            isCopied && "opacity-100 text-green-600",
          )}
          title={isCopied ? "Copied!" : "Copy phone number"}
        >
          <Copy className="h-3 w-3" />
        </button>
      )}
    </>
  )

  if (showLink && phone) {
    const { ...anchorProps } = props as React.HTMLAttributes<HTMLAnchorElement>
    return (
      <a
        href={`tel:${phone}`}
        className={cn(
          "group flex items-center gap-2 text-sm transition-colors hover:text-primary hover:underline",
          className,
        )}
        {...anchorProps}
      >
        {content}
      </a>
    )
  }

  return (
    <div
      className={cn("group flex items-center gap-2 text-sm transition-colors", className)}
      {...props}
    >
      {content}
    </div>
  )
}

export { Phone }
